import { useEffect, useState } from 'react';
import type { HelpdeskRosterCandidate, HelpdeskTelegramGroupInput } from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { createEscalationRosterApi, type EscalationRosterApiBase } from '../api/escalationRosterApi';

type EscalationRosterFieldsProps = {
  agentId: string;
  apiBase: EscalationRosterApiBase;
};

const CHAT_ID_PATTERN = /^-?\d{5,20}$/;

export function EscalationRosterFields({ agentId, apiBase }: EscalationRosterFieldsProps) {
  const rosterApi = createEscalationRosterApi(apiBase);
  const [candidates, setCandidates] = useState<HelpdeskRosterCandidate[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [telegramGroups, setTelegramGroups] = useState<HelpdeskTelegramGroupInput[]>([]);
  const [newGroupChatId, setNewGroupChatId] = useState('');
  const [newGroupLabel, setNewGroupLabel] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingChatId, setTestingChatId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    void Promise.all([rosterApi.getRoster(agentId), rosterApi.listCandidates(agentId)])
      .then(([roster, nextCandidates]) => {
        if (cancelled) {
          return;
        }
        setSelectedUserIds(roster.userIds);
        setTelegramGroups(roster.telegramGroups);
        setCandidates(nextCandidates);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'No se pudo cargar el roster');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, apiBase]);

  const toggleUser = (userId: number) => {
    setSelectedUserIds(current =>
      current.includes(userId)
        ? current.filter(id => id !== userId)
        : current.length >= 20
          ? current
          : [...current, userId],
    );
  };

  const addTelegramGroup = () => {
    const chatId = newGroupChatId.trim().slice(0, 20);
    const label = newGroupLabel.trim().slice(0, 80) || 'Grupo';
    if (!CHAT_ID_PATTERN.test(chatId)) {
      setError('El chatId debe ser numérico (5–20 dígitos, puede empezar con -).');
      return;
    }
    if (telegramGroups.some(group => group.chatId === chatId)) {
      setError('Ese chatId ya está en la lista.');
      return;
    }
    if (telegramGroups.length >= 10) {
      setError('Máximo 10 grupos de Telegram.');
      return;
    }
    setTelegramGroups(current => [...current, { chatId, label }]);
    setNewGroupChatId('');
    setNewGroupLabel('');
    setError('');
  };

  const removeTelegramGroup = (chatId: string) => {
    setTelegramGroups(current => current.filter(group => group.chatId !== chatId));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await rosterApi.putRoster(agentId, {
        userIds: selectedUserIds,
        telegramGroups,
      });
      setSuccess('Roster guardado.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar el roster');
    } finally {
      setSaving(false);
    }
  };

  const handleTestGroup = async (chatId: string) => {
    setTestingChatId(chatId);
    setError('');
    setSuccess('');
    try {
      await rosterApi.testTelegramGroup(agentId, chatId);
      setSuccess(`Ping enviado al grupo ${chatId}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo enviar el ping');
    } finally {
      setTestingChatId(null);
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando roster de escalamiento...</p>;
  }

  return (
    <div className='space-y-6 rounded-xl border p-4'>
      <div>
        <h3 className='text-base font-semibold'>Escalamiento humano</h3>
        <p className='mt-1 text-sm text-muted-foreground'>
          Operadores y grupos de Telegram que reciben aviso cuando el bot deriva a un humano. Si el
          roster queda vacío, se usa el fallback de operadores con Telegram y acceso al bot.
          Agregá el bot de Telegram de esta instancia al grupo. Los mensajes de grupo no abren
          tickets (solo chats privados).
        </p>
      </div>

      <div className='space-y-3'>
        <Label>Operadores ({selectedUserIds.length}/20)</Label>
        {candidates.length === 0 ? (
          <p className='text-sm text-muted-foreground'>
            No hay operadores o administradores con acceso a este bot.
          </p>
        ) : (
          <div className='grid gap-2 md:grid-cols-2'>
            {candidates.map(candidate => {
              const active = selectedUserIds.includes(candidate.id);
              return (
                <button
                  key={candidate.id}
                  type='button'
                  onClick={() => toggleUser(candidate.id)}
                  className={`rounded-lg border p-3 text-left text-sm transition-colors ${
                    active ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'
                  }`}
                >
                  <span className='font-medium'>{candidate.name}</span>
                  <span className='mt-1 block text-xs text-muted-foreground'>
                    {candidate.role}
                    {candidate.hasTelegram ? ' · Telegram configurado' : ' · Sin Telegram'}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className='space-y-3'>
        <Label>Grupos de Telegram ({telegramGroups.length}/10)</Label>
        <div className='flex flex-wrap gap-2'>
          <Input
            placeholder='chatId (ej. -1001234567890)'
            value={newGroupChatId}
            maxLength={20}
            onChange={event => setNewGroupChatId(event.target.value)}
            className='max-w-xs'
          />
          <Input
            placeholder='Etiqueta'
            value={newGroupLabel}
            maxLength={80}
            onChange={event => setNewGroupLabel(event.target.value)}
            className='max-w-xs'
          />
          <Button type='button' variant='outline' onClick={addTelegramGroup}>
            Agregar grupo
          </Button>
        </div>
        {telegramGroups.length === 0 ? (
          <p className='text-sm text-muted-foreground'>Sin grupos configurados.</p>
        ) : (
          <ul className='space-y-2'>
            {telegramGroups.map(group => (
              <li
                key={group.chatId}
                className='flex flex-wrap items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm'
              >
                <span>
                  <strong>{group.label}</strong> · {group.chatId}
                </span>
                <span className='flex gap-2'>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    disabled={testingChatId === group.chatId}
                    onClick={() => void handleTestGroup(group.chatId)}
                  >
                    {testingChatId === group.chatId ? 'Enviando...' : 'Probar ping'}
                  </Button>
                  <Button
                    type='button'
                    size='sm'
                    variant='ghost'
                    onClick={() => removeTelegramGroup(group.chatId)}
                  >
                    Quitar
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {error ? <p className='text-sm text-destructive'>{error}</p> : null}
      {success ? <p className='text-sm text-green-600'>{success}</p> : null}

      <div className='flex justify-end'>
        <Button type='button' disabled={saving} onClick={() => void handleSave()}>
          {saving ? 'Guardando roster...' : 'Guardar roster'}
        </Button>
      </div>
    </div>
  );
}
